import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Brain, Package, DollarSign, Percent, ShieldAlert, Cpu, Save,
  RefreshCw, Loader2, AlertTriangle, CheckCircle2, Sparkles, ArrowLeft,
  Layers, Wallet, BarChart3, Users, Coins,
} from "lucide-react";

/**
 * AdminBusinessBrain — Business Intelligence → Business Brain (Phase 1).
 *
 * The central financial engine. Every calculation on the platform must
 * consume the output of `/api/admin/brain/*`. This page exposes:
 *   1. Master Product Ledger (all products + their brain snapshot)
 *   2. Product Simulator (edit costs/pricing → live green/yellow/red gate)
 *   3. Global Rules (compensation budgets + safety thresholds)
 *
 * Access: super_admin only (backend gate).
 */

const TABS = [
  { key: "ledger", label: "Product Ledger", icon: Package },
  { key: "simulator", label: "Product Simulator", icon: Cpu },
  { key: "rules", label: "Rules & Budgets", icon: ShieldAlert },
];

const PESO = (n) =>
  `₱${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PCT = (n) => `${Number(n || 0).toFixed(2)}%`;

const STATUS_STYLES = {
  green: { text: "text-emerald-300", bg: "bg-emerald-500/12", border: "border-emerald-400/40", label: "PUBLISH READY" },
  yellow: { text: "text-[#FBBF24]", bg: "bg-[#FBBF24]/12", border: "border-[#FBBF24]/40", label: "REVIEW REQUIRED" },
  red: { text: "text-[#FCA5A5]", bg: "bg-[#DC2626]/12", border: "border-[#DC2626]/40", label: "BLOCKED" },
};

// ---------------------------------------------------------------------------
// Field group definitions — mirrors backend module.
// ---------------------------------------------------------------------------
const COST_GROUPS = [
  {
    label: "Fixed Costs (₱ per unit)",
    fields: [
      ["manufacturing_cost", "Manufacturing"],
      ["freight_cost", "Freight / Shipping"],
      ["import_tax", "Import Tax"],
      ["packaging_cost", "Packaging"],
      ["warehouse_handling", "Warehouse Handling"],
      ["storage_cost", "Storage"],
      ["misc_cost", "Miscellaneous"],
    ],
  },
  {
    label: "Variable Costs (% of retail)",
    fields: [
      ["payment_gateway_pct", "Payment Gateway"],
      ["returns_provision_pct", "Returns Provision"],
      ["damaged_goods_reserve_pct", "Damaged Goods Reserve"],
      ["marketing_allocation_pct", "Marketing Allocation"],
      ["promotional_allocation_pct", "Promotional Allocation"],
      ["support_allocation_pct", "Customer Support"],
      ["technology_allocation_pct", "Technology"],
      ["admin_allocation_pct", "Administration"],
    ],
  },
];

const PRICING_FIELDS = [
  ["retail_price", "Retail (SRP)"],
  ["member_price", "Member"],
  ["wholesale_price", "Wholesale"],
  ["distributor_price", "Distributor"],
  ["promotional_price", "Promotional"],
  ["flash_sale_price", "Flash Sale"],
  ["holiday_price", "Holiday"],
  ["bundle_price", "Bundle"],
  ["min_selling_price", "Min Sell Floor"],
  ["max_discount_pct", "Max Discount %"],
];

const BUDGET_FIELDS = [
  ["commission_budget_pct", "Total Commission Budget %"],
  ["promotional_budget_pct", "Promotional Budget %"],
  ["watch_earn_budget_pct", "Watch & Earn Budget %"],
  ["marketing_budget_pct", "Marketing Budget %"],
  ["min_company_net_profit_pct", "Min Company Net Profit %"],
];

const SPLIT_FIELDS = [
  ["split_direct_referral_pct", "Direct Referral"],
  ["split_leadership_l1_pct", "Leadership L1"],
  ["split_leadership_l2_pct", "Leadership L2"],
  ["split_leadership_l3_pct", "Leadership L3"],
  ["split_leadership_l4_pct", "Leadership L4"],
  ["split_leadership_l5_pct", "Leadership L5"],
  ["split_matching_bonus_pct", "Matching Bonus"],
  ["split_global_pool_pct", "Global Pool"],
  ["split_monthly_bonus_pct", "Monthly Bonus"],
];

const RULES_FIELDS = [
  ["min_gross_margin_pct", "Minimum Gross Margin %"],
  ["min_net_margin_pct", "Minimum Net Margin %"],
  ["max_commission_ratio_pct", "Max Commission Ratio %"],
  ["max_discount_ratio_pct", "Max Discount Ratio %"],
  ["max_marketing_pct", "Max Marketing %"],
  ["max_watch_earn_pct", "Max Watch & Earn %"],
  ["max_affiliate_expense_pct", "Max Affiliate Expense %"],
];

// ---------------------------------------------------------------------------

function BrainField({ id, label, value, onChange, disabled }) {
  return (
    <div className="space-y-1" data-testid={`brain-field-${id}`}>
      <label className="text-[10px] uppercase tracking-wider text-white/50">{label}</label>
      <input
        type="number"
        step="0.01"
        min="0"
        value={value ?? 0}
        onChange={(e) => onChange(id, e.target.value)}
        disabled={disabled}
        className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono-num text-white focus:border-[#FBBF24]/60 focus:outline-none disabled:opacity-50"
        data-testid={`brain-input-${id}`}
      />
    </div>
  );
}

function KPICard({ label, value, sublabel, accent = "white", testid }) {
  const map = {
    white: "text-white",
    gold: "text-[#FBBF24]",
    red: "text-[#FCA5A5]",
    green: "text-emerald-300",
    blue: "text-[#93C5FD]",
  };
  return (
    <div className="glass-card p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-white/50">{label}</div>
      <div className={`font-mono-num text-2xl mt-1 ${map[accent]}`}>{value}</div>
      {sublabel && <div className="text-[11px] text-white/40 mt-1">{sublabel}</div>}
    </div>
  );
}

// =============================================================================
// SIMULATOR
// =============================================================================
function Simulator({ initialPricing, initialCosts, globalBudget, globalRules, onSave, savingProduct }) {
  const [pricing, setPricing] = useState(initialPricing || {});
  const [costs, setCosts] = useState(initialCosts || {});
  const [snapshot, setSnapshot] = useState(null);
  const [computing, setComputing] = useState(false);

  useEffect(() => { if (initialPricing) setPricing(initialPricing); }, [initialPricing]);
  useEffect(() => { if (initialCosts) setCosts(initialCosts); }, [initialCosts]);

  const compute = useCallback(async (pr, co) => {
    setComputing(true);
    try {
      const { data } = await api.post("/admin/brain/simulate", {
        pricing: pr, costs: co,
        comp_budget: globalBudget, rules: globalRules,
      });
      setSnapshot(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setComputing(false); }
  }, [globalBudget, globalRules]);

  // Debounced recompute on every edit.
  useEffect(() => {
    const t = setTimeout(() => compute(pricing, costs), 350);
    return () => clearTimeout(t);
  }, [pricing, costs, compute]);

  const setPr = (k, v) => setPricing((p) => ({ ...p, [k]: v === "" ? 0 : Number(v) }));
  const setCo = (k, v) => setCosts((c) => ({ ...c, [k]: v === "" ? 0 : Number(v) }));

  const safety = snapshot?.safety || { status: "yellow", violations: [], warnings: [], can_publish: false };
  const style = STATUS_STYLES[safety.status] || STATUS_STYLES.yellow;

  return (
    <div className="space-y-6" data-testid="brain-simulator">
      {/* Live status banner */}
      <div className={`glass-card ${style.bg} ${style.border} border p-4 flex items-center justify-between flex-wrap gap-3`} data-testid="brain-status-banner">
        <div className="flex items-center gap-3">
          {safety.status === "green" ? <CheckCircle2 className={style.text} size={22} /> : <AlertTriangle className={style.text} size={22} />}
          <div>
            <div className={`text-xs uppercase tracking-widest font-bold ${style.text}`}>{style.label}</div>
            <div className="text-white/60 text-xs mt-0.5">
              {safety.violations.length > 0
                ? `${safety.violations.length} rule violation${safety.violations.length === 1 ? "" : "s"} — publishing blocked`
                : safety.warnings.length > 0
                  ? `${safety.warnings.length} warning${safety.warnings.length === 1 ? "" : "s"} — review before publishing`
                  : "All safety rules passed. Product is ready to publish."}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {computing && <Loader2 className="animate-spin text-white/40" size={16} />}
          {onSave && (
            <button
              onClick={() => onSave(pricing, costs)}
              disabled={savingProduct}
              className="bg-gradient-to-r from-[#FBBF24] to-yellow-300 text-[#0F2042] px-4 py-2 rounded-md text-xs font-bold uppercase tracking-widest hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              data-testid="brain-save-product-btn"
            >
              {savingProduct ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save Product Config
            </button>
          )}
        </div>
      </div>

      {/* Violations / warnings list */}
      {(safety.violations.length > 0 || safety.warnings.length > 0) && (
        <div className="glass-card p-4 space-y-2" data-testid="brain-violations">
          {safety.violations.map((v, i) => (
            <div key={`v-${i}`} className="flex items-start gap-2 text-xs text-[#FCA5A5]">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" /> <span>{v}</span>
            </div>
          ))}
          {safety.warnings.map((w, i) => (
            <div key={`w-${i}`} className="flex items-start gap-2 text-xs text-[#FBBF24]">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" /> <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* Live KPI row */}
      {snapshot && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPICard label="Retail Price" value={PESO(snapshot.pricing.retail_price)} accent="gold" testid="kpi-retail" />
          <KPICard label="Total Landed Cost" value={PESO(snapshot.landed.total_landed_cost)} sublabel={`Fixed ${PESO(snapshot.landed.fixed_costs)} + Var ${PESO(snapshot.landed.variable_costs)}`} testid="kpi-landed" />
          <KPICard label="Gross Margin" value={PCT(snapshot.profit.gross_margin_pct)} sublabel={PESO(snapshot.profit.gross_margin)} accent={snapshot.profit.gross_margin_pct >= (globalRules?.min_gross_margin_pct || 40) ? "green" : "red"} testid="kpi-gross" />
          <KPICard label="Company Net (Target)" value={PCT(snapshot.profit.net_margin_pct)} sublabel={PESO(snapshot.profit.net_margin)} accent="blue" testid="kpi-net" />
          <KPICard label="Contribution Margin" value={PCT(snapshot.profit.contribution_margin_pct)} sublabel={PESO(snapshot.profit.contribution_margin)} testid="kpi-contribution" />
          <KPICard label="Commission Pool" value={PESO(snapshot.compensation.commission_pool)} sublabel={PCT(snapshot.compensation.commission_budget_pct)} accent="gold" testid="kpi-comm-pool" />
          <KPICard label="EBITDA Estimate" value={PCT(snapshot.profit.ebitda_estimate_pct)} sublabel={PESO(snapshot.profit.ebitda_estimate)} testid="kpi-ebitda" />
          <KPICard label="Break-Even Units" value={Number(snapshot.profit.break_even_units).toFixed(2)} sublabel="Per period, given fixed" testid="kpi-be" />
        </div>
      )}

      {/* Editable form + payouts side by side */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="glass-card p-5 space-y-5 lg:col-span-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FBBF24] font-bold flex items-center gap-2">
              <DollarSign size={12} /> Pricing Tiers (₱)
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
              {PRICING_FIELDS.map(([id, label]) => (
                <BrainField key={id} id={id} label={label} value={pricing[id]} onChange={setPr} />
              ))}
            </div>
          </div>
          {COST_GROUPS.map((grp) => (
            <div key={grp.label}>
              <div className="text-[10px] uppercase tracking-widest text-[#FBBF24] font-bold flex items-center gap-2">
                <Package size={12} /> {grp.label}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                {grp.fields.map(([id, label]) => (
                  <BrainField key={id} id={id} label={label} value={costs[id]} onChange={setCo} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Member Earnings Profile — static ₱ (no percentages, per spec) */}
        <div className="glass-card p-5 space-y-3" data-testid="brain-earnings-profile">
          <div className="text-[10px] uppercase tracking-widest text-[#FBBF24] font-bold flex items-center gap-2">
            <Users size={12} /> Partner Earnings Profile
          </div>
          <div className="text-[11px] text-white/40">What Commerce Partners will see on this product&apos;s page. Static ₱ values — never percentages.</div>
          {snapshot?.earnings_profile ? (
            <div className="space-y-2 text-sm">
              {[
                ["Retail Profit", snapshot.earnings_profile.retail_profit_php],
                ["Direct Referral", snapshot.earnings_profile.direct_referral_php],
                ["Level 1", snapshot.earnings_profile.level_1_php],
                ["Level 2", snapshot.earnings_profile.level_2_php],
                ["Level 3", snapshot.earnings_profile.level_3_php],
                ["Level 4", snapshot.earnings_profile.level_4_php],
                ["Level 5", snapshot.earnings_profile.level_5_php],
                ["Matching Bonus", snapshot.earnings_profile.matching_bonus_php],
              ].map(([lb, v]) => (
                <div key={lb} className="flex items-center justify-between border-b border-white/5 pb-1.5" data-testid={`earn-${lb.toLowerCase().replace(/ /g,'-')}`}>
                  <span className="text-white/60 text-xs">{lb}</span>
                  <span className="font-mono-num text-white">{PESO(v)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-1.5" data-testid="earn-zencoins">
                <span className="text-[#FBBF24] text-xs uppercase tracking-widest flex items-center gap-1"><Coins size={12} /> ZenCoins</span>
                <span className="font-mono-num text-[#FBBF24]">{snapshot.earnings_profile.zencoins_earned}</span>
              </div>
            </div>
          ) : (
            <div className="text-white/40 text-xs">Adjust pricing to see earnings.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// PRODUCT LEDGER
// =============================================================================
function ProductLedger({ onEdit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/brain/products");
      setRows(data.products || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const summary = useMemo(() => {
    if (!rows.length) return null;
    const gm = rows.reduce((s, r) => s + (r.profit?.gross_margin_pct || 0), 0) / rows.length;
    const green = rows.filter((r) => r.safety?.status === "green").length;
    const yellow = rows.filter((r) => r.safety?.status === "yellow").length;
    const red = rows.filter((r) => r.safety?.status === "red").length;
    return { avg_gm: gm, green, yellow, red };
  }, [rows]);

  return (
    <div className="space-y-5" data-testid="brain-ledger">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/50">Master Product Ledger</div>
          <div className="text-white/70 text-sm">Every product&apos;s live financial snapshot</div>
        </div>
        <button onClick={load} disabled={loading} className="text-xs px-3 py-2 rounded-md border border-white/15 hover:bg-white/5 flex items-center gap-2" data-testid="ledger-refresh-btn">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPICard label="Products" value={rows.length} testid="ledger-count" />
          <KPICard label="Avg Gross Margin" value={PCT(summary.avg_gm)} accent="gold" testid="ledger-avg-gm" />
          <KPICard label="Publish Ready" value={summary.green} accent="green" testid="ledger-green" />
          <KPICard label="Blocked / Review" value={summary.yellow + summary.red} accent={summary.red > 0 ? "red" : "gold"} testid="ledger-blocked" />
        </div>
      )}

      <div className="glass-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-[#FBBF24]">
              <th className="text-left p-3">Product</th>
              <th className="text-right">Retail</th>
              <th className="text-right">Landed Cost</th>
              <th className="text-right">Gross %</th>
              <th className="text-right">Comm Pool</th>
              <th className="text-center">Config</th>
              <th className="text-center">Status</th>
              <th className="text-right p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center text-white/40 py-6"><Loader2 className="inline animate-spin" size={14} /> Loading...</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="text-center text-white/40 py-6">No products in storefront yet.</td></tr>
            )}
            {rows.map((r) => {
              const st = STATUS_STYLES[r.safety?.status] || STATUS_STYLES.yellow;
              return (
                <tr key={r.product_id} className="border-t border-white/5 hover:bg-white/[0.04]" data-testid={`ledger-row-${r.product_id}`}>
                  <td className="p-3">
                    <div className="text-white font-semibold">{r.name}</div>
                    <div className="text-[11px] text-white/40">{r.category || "uncategorized"}</div>
                  </td>
                  <td className="text-right font-mono-num text-white">{PESO(r.pricing.retail_price)}</td>
                  <td className="text-right font-mono-num text-white/70">{PESO(r.landed.total_landed_cost)}</td>
                  <td className="text-right font-mono-num text-[#FBBF24]">{PCT(r.profit.gross_margin_pct)}</td>
                  <td className="text-right font-mono-num text-white/70">{PESO(r.compensation.commission_pool)}</td>
                  <td className="text-center">
                    <span className={`inline-block text-[10px] px-2 py-0.5 rounded ${r.has_config ? "bg-emerald-500/12 text-emerald-300" : "bg-white/5 text-white/40"}`}>
                      {r.has_config ? "custom" : "default"}
                    </span>
                  </td>
                  <td className="text-center">
                    <span className={`inline-block text-[10px] px-2 py-0.5 rounded uppercase font-bold ${st.bg} ${st.text}`} data-testid={`ledger-status-${r.product_id}`}>
                      {r.safety?.status || "—"}
                    </span>
                  </td>
                  <td className="text-right p-3">
                    <button
                      onClick={() => onEdit(r)}
                      className="text-xs px-3 py-1 rounded border border-white/15 hover:bg-white/10 text-white"
                      data-testid={`ledger-edit-${r.product_id}`}
                    >
                      Configure
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// RULES & BUDGETS
// =============================================================================
function RulesEditor({ config, onSaved }) {
  const [budget, setBudget] = useState(config?.comp_budget || {});
  const [rules, setRules] = useState(config?.rules || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setBudget(config?.comp_budget || {}); setRules(config?.rules || {}); }, [config]);

  const setB = (k, v) => setBudget((b) => ({ ...b, [k]: v === "" ? 0 : Number(v) }));
  const setR = (k, v) => setRules((r) => ({ ...r, [k]: v === "" ? 0 : Number(v) }));

  const splitTotal = SPLIT_FIELDS.reduce((s, [k]) => s + Number(budget[k] || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/brain/config", { comp_budget: budget, rules });
      toast.success("Business Brain rules updated.");
      onSaved(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="brain-rules">
      <div className="glass-card p-5 space-y-4">
        <div className="text-[10px] uppercase tracking-widest text-[#FBBF24] font-bold flex items-center gap-2">
          <Wallet size={12} /> Compensation Allocation Engine
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {BUDGET_FIELDS.map(([id, label]) => (
            <BrainField key={id} id={id} label={label} value={budget[id]} onChange={setB} />
          ))}
        </div>

        <div className="pt-3 border-t border-white/10">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-white/60">Commission Pool Split (must sum to 100%)</div>
            <div className={`text-xs font-mono-num ${Math.abs(splitTotal - 100) < 0.01 ? "text-emerald-300" : "text-[#FCA5A5]"}`} data-testid="split-total">
              Total: {splitTotal.toFixed(2)}%
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {SPLIT_FIELDS.map(([id, label]) => (
              <BrainField key={id} id={id} label={label} value={budget[id]} onChange={setB} />
            ))}
          </div>
        </div>
      </div>

      <div className="glass-card p-5 space-y-4">
        <div className="text-[10px] uppercase tracking-widest text-[#DC2626] font-bold flex items-center gap-2">
          <ShieldAlert size={12} /> Safety Rules (Publish Gates)
        </div>
        <div className="text-[11px] text-white/40">Products violating any of these thresholds will be blocked from publishing.</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {RULES_FIELDS.map(([id, label]) => (
            <BrainField key={id} id={id} label={label} value={rules[id]} onChange={setR} />
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="bg-gradient-to-r from-[#FBBF24] to-yellow-300 text-[#0F2042] px-6 py-3 rounded-md text-xs font-bold uppercase tracking-widest hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
          data-testid="brain-save-rules-btn"
        >
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save Global Rules
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// ROOT
// =============================================================================
export default function AdminBusinessBrain() {
  const [tab, setTab] = useState("ledger");
  const [config, setConfig] = useState(null);          // {comp_budget, rules}
  const [defaults, setDefaults] = useState(null);      // {pricing, costs, comp_budget, rules}
  const [editingProduct, setEditingProduct] = useState(null); // {product_id, pricing, costs}
  const [savingProduct, setSavingProduct] = useState(false);

  const bootstrap = async () => {
    try {
      const [{ data: d }, { data: c }] = await Promise.all([
        api.get("/admin/brain/defaults"),
        api.get("/admin/brain/config"),
      ]);
      setDefaults(d);
      setConfig(c);
    } catch (e) { toast.error(formatErr(e)); }
  };

  useEffect(() => { bootstrap(); }, []);

  const startSimulator = (row) => {
    setEditingProduct(row ? { product_id: row.product_id, name: row.name, pricing: row.pricing, costs: row.costs } : null);
    setTab("simulator");
  };

  const saveProduct = async (pricing, costs) => {
    if (!editingProduct?.product_id) {
      toast.info("Select a product from the Ledger to save. Blank Simulator is for what-if only.");
      return;
    }
    setSavingProduct(true);
    try {
      await api.put(`/admin/brain/products/${editingProduct.product_id}`, { pricing, costs });
      toast.success(`${editingProduct.name}: Business Brain config saved.`);
      setTab("ledger");
      setEditingProduct(null);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSavingProduct(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-business-brain">
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold flex items-center gap-2">
          <Sparkles size={12} /> Business Intelligence
        </div>
        <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight flex items-center gap-3">
          <Brain className="text-[#FBBF24]" size={28} /> Business Brain
        </h2>
        <p className="text-white/55 mt-1 text-sm">The financial operating system. Every product, price, and payout — one source of truth.</p>
      </div>

      <div className="glass-card p-1 inline-flex rounded-lg gap-1 flex-wrap" role="tablist">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-widest flex items-center gap-2 ${tab === t.key ? "bg-gradient-to-r from-[#FBBF24] to-yellow-300 text-[#0F2042]" : "text-white/60 hover:bg-white/5"}`}
              data-testid={`brain-tab-${t.key}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "ledger" && <ProductLedger onEdit={startSimulator} />}

      {tab === "simulator" && defaults && config && (
        <>
          {editingProduct && (
            <div className="flex items-center gap-3 text-sm">
              <button onClick={() => { setEditingProduct(null); setTab("ledger"); }} className="text-white/60 hover:text-white flex items-center gap-1" data-testid="brain-back-to-ledger">
                <ArrowLeft size={14} /> Back to Ledger
              </button>
              <span className="text-white/40">/</span>
              <span className="text-[#FBBF24]">{editingProduct.name}</span>
            </div>
          )}
          <Simulator
            initialPricing={editingProduct?.pricing || defaults.pricing}
            initialCosts={editingProduct?.costs || defaults.costs}
            globalBudget={config.comp_budget}
            globalRules={config.rules}
            onSave={saveProduct}
            savingProduct={savingProduct}
          />
        </>
      )}

      {tab === "rules" && config && <RulesEditor config={config} onSaved={setConfig} />}
    </div>
  );
}
